<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';

	import {
		Button,
		ButtonV2,
		Container,
		Icon,
		IconButton,
		Label,
		LabelV2,
		Stack,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';

	import {
		buildSlots,
		getFirstUnassignedAssignableCameraId,
		getOccupiedCameraIds,
		getSlotCountsFromSettings,
		isCameraAssignableToCloudSlot,
		isHighestCameraCloudStorageTier,
		setCameraSlotList,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import { devices } from 'src/stores/lists/devices';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { homes } from 'src/stores/account/home';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { shellyId16 } from 'src/helpers/interface_helpers';
	import { getDeviceImage } from 'src/helpers/device_state_view_helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import FeaturePromoBanner from 'src/components/FeaturePromoBanner.svelte';
	import type { Device } from 'src/types/Device';
	import type {
		CameraCloudStorageActivePlan,
		CameraCloudStorageSlot,
	} from 'src/types/CameraCloudStorage';

	import SelectCameraForSlotPicker from './SelectCameraForSlotPicker.svelte';
	import CameraSlotAssignmentPreview from './CameraSlotAssignmentPreview.svelte';

	/**
	 * Slot list + camera picker used by ManageCameraCloudStorageModal
	 * and by the Welcome-to-Premium wizard (embedded, no Modal chrome).
	 */
	export let activePlan: CameraCloudStorageActivePlan | null = null;
	/** Open directly on the Add-camera picker (post-subscribe wizard). */
	export let openOnAddStep = false;
	/**
	 * When provided, this camera is already placed in the first empty
	 * slot (draft-only — nothing is saved until the user clicks Save).
	 */
	export let preselectedCameraId: string | undefined;
	/**
	 * Welcome-to-Premium wizard: gold included-slots banner, no CCS
	 * upsell, and the first unassigned camera is drafted into the first
	 * empty slot. Other manage-storage surfaces keep the existing UI.
	 */
	export let welcomeWizard = false;
	/**
	 * When true, picker Cancel/Select buttons render inside this panel
	 * so a parent wizard can keep Skip / Save (or Save and continue)
	 * in its own footer on the main view.
	 */
	export let showPickerActions = false;
	/** Dev Utilities flow preview */
	export let slotCountOverride: number | null = null;
	export let previewMode = false;

	const dispatch = createEventDispatcher<{
		close: void;
		upgrade: void;
	}>();

	export let view: 'main' | 'add' | 'replace' = 'main';
	let editingSlotIndex: number | null = null;
	export let pickerSelection: string[] = [];
	let draftAssignments: Record<number, string | null> = {};
	export let saving = false;
	export let hasDirtyChanges = false;

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	$: isPremium = ($user_data?.premium ?? 0) > 0;
	/** `camera_slots.all` is the source of truth (Premium slots stack across subscriptions). */
	$: totalSlots = slotCountOverride ?? getSlotCountsFromSettings($user_settings).all;
	$: slots = buildSlots(activePlan, isPremium, draftAssignments, totalSlots);
	$: usedSlots = slots.filter((slot) => slot.cameraId).length;
	$: freeSlotsCount = Math.max(0, totalSlots - usedSlots);
	$: occupiedIds = getOccupiedCameraIds(draftAssignments);
	$: premiumOnlyNoPlans = isPremium && !activePlan;
	$: canUpgradePlan = !isHighestCameraCloudStorageTier(activePlan?.tier);
	$: isSharedHome = isActiveHomeShared($homes.list);
	$: hasDirtyChanges = !isSameAssignments(draftAssignments, $camera_cloud_storage.slotAssignments);
	$: preselectedSlot = preselectedCameraId
		? slots.find((slot) => slot.cameraId === preselectedCameraId)
		: undefined;
	$: preselectedDevice = preselectedSlot ? resolveDevice(preselectedSlot.cameraId) : undefined;
	$: preselectedSlotIndex = preselectedSlot?.index;
	$: visibleSlots =
		preselectedSlotIndex != null
			? slots.filter((slot) => slot.index !== preselectedSlotIndex)
			: slots;
	$: replaceInitialSelection =
		editingSlotIndex != null && draftAssignments[editingSlotIndex]
			? [draftAssignments[editingSlotIndex] as string]
			: ([] as string[]);
	/** Promo-banner assign flow: only the camera → slot preview, like Setup Cloud Recordings. */
	$: isAssignCameraView = Boolean(preselectedCameraId) && !welcomeWizard;

	onMount(() => {
		draftAssignments = { ...$camera_cloud_storage.slotAssignments };
		if (openOnAddStep) {
			view = 'add';
			pickerSelection = [];
		}
		if (preselectedCameraId) {
			const currentSlots = buildSlots(activePlan, isPremium, draftAssignments, totalSlots);
			const alreadyAssigned = currentSlots.some((s) => s.cameraId === preselectedCameraId);
			const preselectedDevice = resolveDevice(preselectedCameraId);
			if (!alreadyAssigned && isCameraAssignableToCloudSlot(preselectedDevice)) {
				const firstEmpty = currentSlots.find((s) => !s.cameraId);
				if (firstEmpty) {
					draftAssignments = { ...draftAssignments, [firstEmpty.index]: preselectedCameraId };
				}
			}
		} else if (welcomeWizard) {
			assignFirstUnassignedCameraToFirstEmptySlot();
		}
	});

	function assignFirstUnassignedCameraToFirstEmptySlot() {
		const cameraId = getFirstUnassignedAssignableCameraId($devices ?? {}, draftAssignments);
		if (!cameraId) return;
		const currentSlots = buildSlots(activePlan, isPremium, draftAssignments, totalSlots);
		const firstEmpty = currentSlots.find((s) => !s.cameraId);
		if (!firstEmpty) return;
		draftAssignments = { ...draftAssignments, [firstEmpty.index]: cameraId };
	}

	function isSameAssignments(
		a: Record<number, string | null>,
		b: Record<number, string | null>,
	): boolean {
		const aKeys = Object.keys(a);
		const bKeys = Object.keys(b);
		if (aKeys.length !== bKeys.length) return false;
		for (const key of aKeys) {
			if (a[Number(key)] !== b[Number(key)]) return false;
		}
		return true;
	}

	/**
	 * Look up a device from `$devices` by camera ID. The `camera_list`
	 * from the backend stores decimal device IDs, but `$devices` is
	 * keyed by hex IDs (via `convert_array_to_object(devs, 'id')`).
	 * Try the raw key first, then fall back to `shellyId16(cameraId)`
	 * which converts decimal → padded hex.
	 */
	function resolveDevice(cameraId: string | null): Device | undefined {
		if (!cameraId) return undefined;
		return ($devices?.[cameraId] ?? $devices?.[shellyId16(cameraId)]) as Device | undefined;
	}

	function getCameraName(cameraId: string | null): string {
		if (!cameraId) return '';
		return resolveDevice(cameraId)?.name ?? cameraId;
	}

	function getCameraDevice(cameraId: string | null): Device | undefined {
		return resolveDevice(cameraId);
	}

	/**
	 * Edit/delete on occupied slots is allowed on owned units, including
	 * cameras shown as a raw id. Shared homes (sub-units) are read-only.
	 */
	function canManageAssignedCamera(cameraId: string | null): boolean {
		if (!cameraId) return false;
		return !isSharedHome;
	}

	export function startAdd() {
		if (isSharedHome) return;
		view = 'add';
		pickerSelection = [];
	}

	function startReplace(slot: CameraCloudStorageSlot) {
		if (isSharedHome) return;
		if (slot.cameraId && !canManageAssignedCamera(slot.cameraId)) return;
		editingSlotIndex = slot.index;
		pickerSelection = slot.cameraId ? [slot.cameraId] : [];
		view = 'replace';
	}

	function removeFromSlot(slot: CameraCloudStorageSlot) {
		if (!slot.cameraId) return;
		if (!canManageAssignedCamera(slot.cameraId)) return;
		const next = { ...draftAssignments };
		delete next[slot.index];
		draftAssignments = next;
	}

	function onPickerChange(event: CustomEvent<{ cameraIds: string[] }>) {
		pickerSelection = event.detail.cameraIds;
	}

	export function applyAdd() {
		const next = { ...draftAssignments };
		const emptyIndexes = slots.filter((s) => !s.cameraId).map((s) => s.index);
		pickerSelection.forEach((cameraId, i) => {
			const slotIndex = emptyIndexes[i];
			if (slotIndex == null) return;
			if (!isCameraAssignableToCloudSlot(resolveDevice(cameraId))) return;
			next[slotIndex] = cameraId;
		});
		draftAssignments = next;
		view = 'main';
		pickerSelection = [];
	}

	/** Applies the picked camera to `editingSlotIndex` and removes it from any other slot. */
	export function applyReplace() {
		if (editingSlotIndex == null) return;
		const next = { ...draftAssignments };
		if (pickerSelection.length === 0) {
			delete next[editingSlotIndex];
		} else {
			const [newCameraId] = pickerSelection;
			if (!isCameraAssignableToCloudSlot(resolveDevice(newCameraId))) return;
			for (const key of Object.keys(next)) {
				if (next[Number(key)] === newCameraId) delete next[Number(key)];
			}
			next[editingSlotIndex] = newCameraId;
		}
		draftAssignments = next;
		view = 'main';
		editingSlotIndex = null;
		pickerSelection = [];
	}

	export function cancelPicker() {
		if (openOnAddStep) {
			dispatch('close');
			return;
		}
		view = 'main';
		editingSlotIndex = null;
		pickerSelection = [];
	}

	/**
	 * POSTs occupied slot ids to `set_camera_list`. On success updates
	 * the store to match `draftAssignments`. Returns false when the
	 * request failed so a parent wizard can stay on this step.
	 */
	export async function saveAssignments(): Promise<boolean> {
		if (saving) return false;
		if (!hasDirtyChanges) return true;
		if (previewMode) return true;

		saving = true;
		const deviceIds = Array.from(getOccupiedCameraIds(draftAssignments));
		const result = await setCameraSlotList(deviceIds);
		saving = false;

		if (result.error) {
			toast.open({
				type: 'danger',
				title: $format(result.error.key, {
					default: result.error.default,
					values: { ids: result.error.ids?.join(', ') ?? '' },
				}),
			});
			return false;
		}

		camera_cloud_storage.setAssignments(draftAssignments);
		toast.open({
			type: 'success',
			title: $format('toast_camera_cloud_storage_assignments_saved', {
				default: 'Camera Cloud Storage slots updated.',
			}),
		});
		return true;
	}
</script>

<div class="ccs-modal-content">
	{#if view === 'main'}
		{#if isAssignCameraView}
			<FeaturePromoBanner tone="camera" icon="fas fa-camera" showAction={false} class="mb-m">
				<Text styleType="controls-large" class="feature-promo-banner__title">
					{$format('alert_assign_camera_slot_available_heading', {
						default: 'A Camera Cloud Storage slot is available',
					})}
				</Text>
				<Text styleType="body-small" class="feature-promo-banner__text">
					{$format('activate_storage_ready_desc', {
						default:
							'Assign this camera to your available slot now. Recording begins after the assignment is saved.',
					})}
				</Text>
			</FeaturePromoBanner>
		{:else if welcomeWizard}
			<FeaturePromoBanner tone="premium" icon="fas fa-gem" class="mb-m">
				<Text styleType="controls-large" class="feature-promo-banner__title">
					{#if totalSlots > 1}
						{$format('alert_welcome_premium_camera_slots_heading', {
							default:
								'Your Premium subscription includes Camera Cloud Storage for one more Shelly Camera.',
						})}
					{:else}
						{$format('alert_welcome_premium_camera_slot_heading', {
							default:
								'Your Premium subscription includes Camera Cloud Storage for one Shelly Camera',
						})}
					{/if}
				</Text>
				<Text styleType="body-small" class="feature-promo-banner__text">
					{$format('alert_welcome_premium_camera_slots_message', {
						default:
							'Select a camera below, then save to confirm. Got more Shelly cameras to cover? Add a Camera Cloud Storage plan for extra storage.',
					})}
				</Text>
			</FeaturePromoBanner>
		{:else if activePlan}
			<FeaturePromoBanner tone="camera" icon="fas fa-camera" showAction={canUpgradePlan}>
				<Text styleType="controls-large" class="feature-promo-banner__title">
					{$format('text_camera_cloud_subscription_title', {
						default: `Camera Cloud subscription - ${activePlan.tier} Cameras`,
						values: { count: activePlan.tier },
					})}
				</Text>
				<Text styleType="body-small" class="feature-promo-banner__text">
					{$format('text_camera_cloud_storage_usage_summary', {
						default: `You are currently using ${usedSlots} of ${totalSlots} Camera Cloud Storage slots`,
						values: { used: usedSlots, total: totalSlots },
					})}
				</Text>
				<Button
					slot="action"
					class="feature-promo-banner__button"
					size="m"
					width="full"
					on:click={() => dispatch('upgrade')}
				>
					{$format('button_subscription_upgrade', { default: 'Upgrade' })}
				</Button>
			</FeaturePromoBanner>
		{:else if isPremium}
			<FeaturePromoBanner tone="camera" icon="fas fa-gem" class="ccs-premium-slot-summary mb-m">
				{#if totalSlots > 1}
					<Text styleType="controls-large" class="feature-promo-banner__title">
						{$format('alert_camera_cloud_storage_premium_slots_heading', {
							default: `You have ${totalSlots} Camera Cloud Storage slots included in your Premium subscription`,
							values: { count: totalSlots },
						})}
					</Text>
					<Text styleType="body-small" class="feature-promo-banner__text">
						{$format('text_camera_cloud_storage_usage_summary', {
							default: `You are currently using ${usedSlots} of ${totalSlots} Camera Cloud Storage slots`,
							values: { used: usedSlots, total: totalSlots },
						})}
					</Text>
				{:else}
					<Text styleType="controls-large" class="feature-promo-banner__title">
						{$format('alert_camera_cloud_storage_premium_only_heading', {
							default:
								'You have 1 Camera Cloud Storage slot included in your Premium subscription',
						})}
					</Text>
					<Text styleType="body-small" class="feature-promo-banner__text">
						{$format('alert_camera_cloud_storage_premium_only_message', {
							default:
								'Your Premium subscription includes 1 Camera Cloud Storage slot. Subscribe to a Camera Cloud Storage plan to add more.',
						})}
					</Text>
				{/if}
			</FeaturePromoBanner>
		{/if}

		{#if preselectedDevice && preselectedSlot}
			<Text
				styleType="body-small"
				class={`text-uppercase text-color-muted text-bold mb-s${isAssignCameraView ? '' : ' mt-m'}`}
			>
				{$format('text_camera_slot_assignment', { default: 'Camera assignment' })}
			</Text>
			<div class={isAssignCameraView ? '' : 'mb-m'}>
				<CameraSlotAssignmentPreview
					device={preselectedDevice}
					slotIndex={preselectedSlot.index}
				/>
			</div>
		{/if}

		{#if !isAssignCameraView && visibleSlots.length}
			<Text styleType="body-small" class="text-uppercase text-color-muted text-bold mb-s mt-m">
				{$format('text_subscribed_cameras', { default: 'Subscribed cameras' })}
			</Text>

			<Stack gap="xs">
				{#each visibleSlots as slot (slot.index)}
					{@const slotDevice = getCameraDevice(slot.cameraId)}
					{@const slotImage = slotDevice ? getDeviceImage(slotDevice) : undefined}
					{@const canManage = Boolean(slot.cameraId) && !isSharedHome}
					<div class="ccs-slot-row" class:ccs-slot-row--empty={!slot.cameraId}>
						{#if slot.cameraId}
							<div class="d-flex align-items-center gap-s flex-1 min-width-0">
								<div class="ccs-device-avatar">
									{#if slotImage}
										<img src={slotImage} alt={getCameraName(slot.cameraId)} />
									{:else}
										<Icon icon="fas fa-camera" />
									{/if}
								</div>
								<div class="flex-1 min-width-0">
									<Text styleType="body-small" class="text-bold">
										{getCameraName(slot.cameraId)}
									</Text>
									<div class="d-flex gap-xs mt-xs">
										{#if slot.source === 'premium'}
											{#if $showNewLayout}
												<LabelV2
													size="s"
													value={$format('label_premium_subscription', {
														default: 'Premium Subscription',
													})}
													icon="fas fa-gem"
													backgroundColor="--color-premium-10"
												/>
											{:else}
												<Label
													size="s"
													value={$format('label_premium_subscription', {
														default: 'Premium Subscription',
													})}
													icon="fas fa-gem"
													type="premium"
												/>
											{/if}
										{:else if $showNewLayout}
											<LabelV2
												size="s"
												value={$format('label_camera_subscription', {
													default: 'Camera Subscription',
												})}
												icon="fas fa-camera"
											/>
										{:else}
											<Label
												size="s"
												value={$format('label_camera_subscription', {
													default: 'Camera Subscription',
												})}
												icon="fas fa-camera"
											/>
										{/if}
									</div>
								</div>
							</div>
							{#if canManage}
								<div class="ccs-slot-actions">
									<IconButton
										icon="fas fa-pen"
										aria-label={$format('label_replace_camera_in_slot', {
											default: 'Replace camera in this slot',
										})}
										on:click={() => startReplace(slot)}
									/>
									<IconButton
										icon="fas fa-trash"
										variant="danger"
										aria-label={$format('label_remove_camera_from_slot', {
											default: 'Remove camera from this slot',
										})}
										on:click={() => removeFromSlot(slot)}
									/>
								</div>
							{/if}
						{:else}
							<button
								type="button"
								class="ccs-empty-slot"
								disabled={isSharedHome}
								on:click={() => startReplace(slot)}
								aria-label={$format('label_add_camera_to_slot', {
									default: 'Add camera to this slot',
								})}
							>
								<Icon icon="fas fa-plus" />
								<Text styleType="body-small" class="text-color-muted">
									{$format('label_empty_slot', { default: 'Empty slot' })}
								</Text>
							</button>
						{/if}
					</div>
				{/each}
			</Stack>
		{/if}

		{#if !isAssignCameraView && freeSlotsCount > 0}
			<div class="mt-m d-flex">
				<svelte:component
					this={ButtonComp}
					variant="primary"
					size="m"
					icon="fas fa-plus"
					disabled={isSharedHome}
					on:click={startAdd}
				>
					{$format('button_fill_empty_camera_slots', {
						default: 'Fill the empty camera slots',
					})}
				</svelte:component>
			</div>
		{:else if premiumOnlyNoPlans && !welcomeWizard}
			<div class="mt-m">
				<div class="ccs-upgrade-copy mb-s">
					<Text styleType="controls-large" class="text-bold">
						{$format('text_camera_cloud_storage_more_cameras_heading', {
							default: 'Need storage for more cameras?',
						})}
					</Text>
					<Text styleType="body-small" class="text-color-muted">
						{$format('text_camera_cloud_storage_more_cameras_message', {
							default:
								'Add a Camera Cloud Storage plan to protect every view and keep more recordings securely in the cloud.',
						})}
					</Text>
				</div>
				<div class="d-flex">
					<Button
						class="ccs-plan-button ccs-plan-button--purple"
						size="m"
						variant="secondary"
						on:click={() => dispatch('upgrade')}
					>
						{$format('button_promo_subscribe_now', {
							default: 'Subscribe now',
						})}
					</Button>
				</div>
			</div>
		{/if}
	{:else if view === 'add'}
		<Container padding="0">
			<Text styleType="body-small" class="text-color-muted mb-s">
				{$format('text_select_cameras_for_slots', {
					default: `Select up to ${freeSlotsCount} camera${freeSlotsCount === 1 ? '' : 's'} to fill your free slots.`,
					values: { count: freeSlotsCount },
				})}
			</Text>
			<SelectCameraForSlotPicker
				mode="multi"
				maxSelection={freeSlotsCount}
				occupiedCameraIds={occupiedIds}
				initialSelection={[]}
				on:change={onPickerChange}
			/>
		</Container>
	{:else if view === 'replace'}
		<Container padding="0">
			<Text styleType="body-small" class="text-color-muted mb-s">
				{$format('text_replace_camera_in_slot_help', {
					default: 'Pick the camera that should occupy this slot.',
				})}
			</Text>
			<SelectCameraForSlotPicker
				mode="single"
				maxSelection={1}
				occupiedCameraIds={occupiedIds}
				initialSelection={replaceInitialSelection}
				on:change={onPickerChange}
			/>
		</Container>
	{/if}
</div>

{#if showPickerActions && view !== 'main'}
	<div class="d-flex justify-content-end gap-s mt-m">
		<svelte:component this={ButtonComp} variant="secondary" size="m" on:click={cancelPicker}>
			{$format('button_cancel', { default: 'Cancel' })}
		</svelte:component>
		<svelte:component
			this={ButtonComp}
			variant="primary"
			size="m"
			disabled={pickerSelection.length === 0}
			on:click={() => (view === 'add' ? applyAdd() : applyReplace())}
		>
			{$format('button_select_camera', { default: 'Select camera' })}
		</svelte:component>
	</div>
{/if}

<style>
	.flex-1 {
		flex: 1;
	}

	.min-width-0 {
		min-width: 0;
	}

	/*
	 * Modal's content slot is the scroll container — `removeScrollPadding`
	 * strips its default inset, which leaves the body flush with the
	 * scrollbar. A small inline-end padding restores a comfortable gap
	 * between the slot rows and the vertical scrollbar.
	 */
	.ccs-modal-content {
		--ccs-upgrade-surface: rgba(219, 92, 244, 0.1);
		padding-inline-end: 8px;
		padding-left: 15px;
		padding-right: 15px;
	}

	:global(.ccs-premium-slot-summary) {
		background: var(--ccs-upgrade-surface) !important;
	}

	.ccs-upgrade-copy {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.ccs-slot-row {
		display: flex;
		align-items: center;
		gap: 12px;
		background: var(--color-secondary-1, #242527);
		border: 1px solid var(--color-secondary-2, #303235);
		border-radius: 12px;
		padding: 12px;
	}

	/*
	 * Square device thumbnail used in slot rows. The inner <img> fills
	 * the box and crops with `object-fit: cover` so different device
	 * artwork aspect ratios still look uniform.
	 */
	.ccs-device-avatar {
		width: 36px;
		height: 36px;
		border-radius: 8px;
		background: var(--color-secondary-2, #303235);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		flex-shrink: 0;
	}

	.ccs-device-avatar img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.ccs-slot-row--empty {
		padding: 0;
		background: transparent;
		border: none;
	}

	.ccs-slot-actions {
		display: inline-flex;
		gap: 4px;
		flex-shrink: 0;
	}

	.ccs-empty-slot {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
		background: transparent;
		border: 1px dashed var(--color-secondary-3, #5b5f66);
		border-radius: 12px;
		padding: 16px;
		color: inherit;
		cursor: pointer;
		transition: border-color 0.2s ease-in-out;
	}

	.ccs-empty-slot:hover:not(:disabled) {
		border-color: #893bf2;
	}

	.ccs-empty-slot:disabled {
		cursor: not-allowed;
		opacity: 0.55;
	}

	.gap-xs {
		gap: 4px;
	}

	.mt-xs {
		margin-top: 4px;
	}
</style>
